import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage } from '../../common';
// import LoadingSpinner from '../../ui/LoadingSpinner'; // No longer needed here
import { useCalculateShippingRatesMutation, useFetchPickupPointsMutation } from '../../../redux/apis/endpoints/deliveryApi';
import { 
  setShippingGroupRate,
  setShippingAddressGroup,
  setShippingGroupRateByAddress,
  setSelectedShippingRate,
  // removeUnshippableItems, // Not used in this component directly
  // clearUnshippableItems // Not used in this component directly
} from '../../../redux/slices/checkoutSlice';
import { updateCartItemDeliveryDetails } from '../../../redux/slices/cartSlice';
// import { debounce } from 'lodash'; // Not used
// import { getComparableDayValue } from './utils'; // Not used directly, handled by hook
import ItemThumbnails from './components/ItemThumbnails';
import ShippingRateOptions from './components/ShippingRateOptions';
import { useShippingRateCalculator } from '../../../hooks/useShippingRateCalculator';

const ShippingMethodSection = ({ 
  addressId = 'default', 
  title, 
  items, 
  onComplete, 
  setError, 
  // transactionType, // Not used
  sharedAddress,
  // transactionType, // + Receive transactionType
}) => {
  const dispatch = useDispatch();
  const checkout = useSelector(state => state.checkout);
  const { checkoutCurrency } = checkout;
  
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [displayRates, setDisplayRates] = useState([]);
  const allRawRatesFromApi = useRef([]);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectedPickupLocationId, setSelectedPickupLocationId] = useState(null); // For future use

  const [fetchPickupPoints, { isLoading: isLoadingPickupPoints /*, error: pickupPointsApiError */ }] = useFetchPickupPointsMutation();

  // --- Logic to fetch pickup points if a rate is a pickup service ---
  const fetchPickupPointsForRate = useCallback(async (rateObject) => {
    if (!rateObject) {
      setPickupLocations([]);
      setSelectedPickupLocationId(null);
      return;
    }

    const isPickupService = rateObject.servicelevel?.token === 'colissimo_pick_up_point';

    if (isPickupService && sharedAddress) {
      console.log('Pickup service active. Fetching pickup points for token:', rateObject.servicelevel.token);
      console.log('Original sharedAddress:', JSON.stringify(sharedAddress, null, 2)); // Keep original log for comparison

      const mappedAddress = {
        street1: sharedAddress.street, // Map street to street1
        street2: sharedAddress.apartment || '', // Map apartment to street2, or empty string
        city: sharedAddress.city,
        postalCode: sharedAddress.postalCode,
        country: sharedAddress.country,
      };
      console.log('Mapped customerAddress being sent to backend:', JSON.stringify(mappedAddress, null, 2));

      try {
        const points = await fetchPickupPoints({
          customerAddress: mappedAddress, // Use the mapped address
          carrierToken: rateObject.servicelevel.token,
        }).unwrap();

        setPickupLocations(points);
        console.log('Fetched pickup points:', points);
        if (points && points.length > 0) {
          console.log(`Successfully fetched ${points.length} pickup locations.`);
          // TODO: Auto-select first point or implement selection UI
        } else {
          console.log('No pickup locations found for this service and address.');
          // setError('No pickup locations available for this shipping option.'); // Optionally set error
        }
      } catch (err) {
        console.error('Failed to fetch pickup points:', err);
        setError(err.data?.message || err.message || 'Could not fetch pickup locations.');
        setPickupLocations([]);
      }
    } else if (isPickupService && !sharedAddress) {
      console.warn('Pickup service selected, but no shared address available to fetch points.');
      setPickupLocations([]); // Clear points if address becomes unavailable
      // setError('Please ensure an address is selected to find pickup locations.');
    } else {
      // Not a pickup service, or conditions not met, ensure pickup locations are cleared
      setPickupLocations([]);
      setSelectedPickupLocationId(null);
    }
  }, [sharedAddress, fetchPickupPoints, /* setPickupLocations, setSelectedPickupLocationId, */ setError]); // Removed setPickupLocations and setSelectedPickupLocationId from deps as they are setters

  // --- Callbacks for the useShippingRateCalculator hook ---
  const handleCalculationStart = useCallback(() => {
    // setError(null); // Optionally clear parent error
  }, [/* setError */]);

  const handleRatesSuccess = useCallback((formattedRatesFromHook, rawRatesFromHook) => {
    // Filter out Colissimo pick_up_point (retrait) options
    const filteredRawRates = rawRatesFromHook.filter(rate => {
      const provider = rate.provider?.toLowerCase();
      const serviceLevelName = rate.servicelevel?.name?.toLowerCase();
      const serviceLevelToken = rate.servicelevel?.token?.toLowerCase();

      const isColissimo = provider === 'colissimo' || serviceLevelName?.includes('colissimo');
      const isPickupPoint = serviceLevelToken?.includes('pick_up_point') || serviceLevelToken?.includes('retrait');

      if (isColissimo && isPickupPoint) {
        console.log('Filtering out Colissimo pick_up_point/retrait rate:', rate);
        return false; // Exclude this rate
      }
      return true; // Include this rate
    });

    const filteredFormattedRates = formattedRatesFromHook.filter(formattedRate => {
      // Ensure the formatted rate corresponds to a raw rate that wasn't filtered out
      return filteredRawRates.some(rawRate => (rawRate.id || rawRate.object_id) === formattedRate.id);
    });

    setDisplayRates(filteredFormattedRates);
    allRawRatesFromApi.current = filteredRawRates;
    console.log('All raw rates from API (Shippo) after filtering:', filteredRawRates);
    setError(null);

    const currentSelectedRateFromRedux = checkout.deliveryGroups?.shipping?.addressGroups?.[addressId]?.rate;
    const reduxRateId = currentSelectedRateFromRedux?.id || currentSelectedRateFromRedux?.object_id;

    let rateToSelectByDefault = null;
    let rateIdToSet = null;

    if (filteredFormattedRates.length > 0) {
      if (reduxRateId && filteredFormattedRates.some(fr => fr.id === reduxRateId)) {
        rateIdToSet = reduxRateId;
        rateToSelectByDefault = filteredFormattedRates.find(r => r.id === reduxRateId);
      } else {
        const cheapestFormattedRate = filteredFormattedRates[0];
        rateIdToSet = cheapestFormattedRate.id;
        rateToSelectByDefault = cheapestFormattedRate;
      }
    }
    
    setSelectedRateId(rateIdToSet);

    if (rateToSelectByDefault) {
      dispatch(setShippingGroupRateByAddress({ addressId, rate: rateToSelectByDefault }));
      dispatch(setShippingGroupRate({ rate: rateToSelectByDefault }));
      dispatch(setSelectedShippingRate(rateToSelectByDefault));
      
      // +++ Update individual cart items +++
      const shippoRateId = rateToSelectByDefault.id; // This should be the Shippo object_id
      items.forEach(item => {
        dispatch(updateCartItemDeliveryDetails({
          itemIdentifier: item.cartItemIndex, // Assuming cartItemIndex is present
          cartItemIndexUsed: true,
          deliveryMethod: 'shipping',
          selectedShippoRateId: shippoRateId,
          itemType: item.type, // Use item.type directly from the looped item
        }));
      });
      // ++++++++++++++++++++++++++++++++++
      
      const rawRateForPickup = allRawRatesFromApi.current.find(r => (r.id || r.object_id) === rateToSelectByDefault.id);
      fetchPickupPointsForRate(rawRateForPickup || rateToSelectByDefault);

      if (onComplete) {
        onComplete(true);
      }
    } else if (filteredFormattedRates.length === 0 && filteredRawRates.length > 0) {
      // setError('No suitable shipping options found from the available rates.');
      // If raw rates were returned but filtered to none, consider these items unshippable for this group.
      setError('No suitable shipping options found from the available rates for this address.');
      if (onComplete) {
        onComplete(false, { unshippable: true, addressId, items: items.map(item => item.id || item.cartItemIndex) });
      }
    } else if (filteredFormattedRates.length === 0) {
      setError('No shipping options are available for this address. Please try a different address.');
      // If no rates were returned at all, consider these items unshippable.
      if (onComplete) {
        onComplete(false, { unshippable: true, addressId, items: items.map(item => item.id || item.cartItemIndex) });
      }
    }
  }, [dispatch, addressId, checkout.deliveryGroups?.shipping?.addressGroups, setError, fetchPickupPointsForRate, onComplete, items]);

  const handleRatesUnshippable = useCallback((unshippableItemIds, message) => {
    setError(message || 'Some items cannot be shipped to this address.');
    setDisplayRates([]);
    allRawRatesFromApi.current = [];
    setSelectedRateId(null);
    setPickupLocations([]); // Clear pickup points
    setSelectedPickupLocationId(null);
    if (onComplete) {
      onComplete(false, { unshippable: true, addressId, items: unshippableItemIds });
    }
  }, [setError, onComplete, addressId]);

  const handleRatesError = useCallback((message) => {
    const errorMessage = message || 'Unable to fetch shipping rates. Please try again.';
    console.error('Shipping rates error:', errorMessage);
    setError(errorMessage);
    setDisplayRates([]);
    allRawRatesFromApi.current = [];
    setSelectedRateId(null);
    setPickupLocations([]); // Clear pickup points
    setSelectedPickupLocationId(null);
    // Also mark items in this group as unshippable due to the error
    if (onComplete) {
      onComplete(false, { unshippable: true, addressId, items: items.map(item => item.id || item.cartItemIndex) });
    }
  }, [setError, onComplete, addressId, items]);

  const {
    isLoading: combinedRatesLoading,
  } = useShippingRateCalculator({
    customerAddress: sharedAddress,
    items,
    addressId,
    currency: checkoutCurrency,
    onCalculationStart: handleCalculationStart,
    onRatesSuccess: handleRatesSuccess,
    onRatesUnshippable: handleRatesUnshippable,
    onRatesError: handleRatesError,
  });

  useEffect(() => {
    if (sharedAddress) {
      dispatch(setShippingAddressGroup({
        addressId,
        address: sharedAddress,
        items
      }));
      // If sharedAddress changes, and a rate is already selected, re-evaluate fetching pickup points
      const currentSelectedRawRate = allRawRatesFromApi.current.find(r => (r.id || r.object_id) === selectedRateId);
      if (currentSelectedRawRate) {
         fetchPickupPointsForRate(currentSelectedRawRate);
      }
    }
  }, [sharedAddress, addressId, items, dispatch, selectedRateId, fetchPickupPointsForRate]);
  
  const handleRateSelection = async (newRateId) => {
    setSelectedRateId(newRateId);
    const selectedFormattedRate = displayRates.find(r => r.id === newRateId);
    
    if (selectedFormattedRate) {
      dispatch(setShippingGroupRateByAddress({ addressId, rate: selectedFormattedRate }));
      dispatch(setShippingGroupRate({ rate: selectedFormattedRate }));
      dispatch(setSelectedShippingRate(selectedFormattedRate));
      
      // +++ Update individual cart items +++
      const shippoRateId = selectedFormattedRate.id; // This should be the Shippo object_id
      items.forEach(item => {
        dispatch(updateCartItemDeliveryDetails({
          itemIdentifier: item.cartItemIndex, // Assuming cartItemIndex is present
          cartItemIndexUsed: true,
          deliveryMethod: 'shipping',
          selectedShippoRateId: shippoRateId,
          itemType: item.type, // Use item.type directly from the looped item
        }));
      });
      // ++++++++++++++++++++++++++++++++++
      
      const rawRateForPickupOnClick = allRawRatesFromApi.current.find(r => (r.id || r.object_id) === selectedFormattedRate.id);
      fetchPickupPointsForRate(rawRateForPickupOnClick || selectedFormattedRate);

      if (onComplete) {
        onComplete(true);
      }
    }
  };
  
  useEffect(() => {
    const isComplete = !!(sharedAddress && selectedRateId);
    // TODO: Modify completion logic if a pickup point selection becomes mandatory
    // For now, just selecting a rate is enough for this section's 'completeness'.
    // const isPickupRate = allRawRatesFromApi.current.find(r => (r.id || r.object_id) === selectedRateId)?.servicelevel?.token === 'colissimo_pick_up_point';
    // const isComplete = !!(sharedAddress && selectedRateId && (!isPickupRate || (isPickupRate && selectedPickupLocationId)));
    if (onComplete) {
      onComplete(isComplete, { addressId });
    }
  }, [sharedAddress, selectedRateId, onComplete, addressId /*, selectedPickupLocationId */]);
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="font-medium text-lg mb-3">{title}</h3>
      <ItemThumbnails items={items} />
      <ShippingRateOptions 
        sharedAddress={sharedAddress}
        loading={combinedRatesLoading}
        ratesLoading={combinedRatesLoading}
        shippingRates={displayRates}
        selectedRate={selectedRateId}
        handleRateSelection={handleRateSelection}
      />
      {isLoadingPickupPoints && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">Finding pickup locations...</p>
        </div>
      )}
      {pickupLocations && pickupLocations.length > 0 && (
        <div className="mt-4 p-4 border border-gray-200 rounded-md">
          <h4 className="font-medium text-md mb-2">Select a Pickup Point:</h4>
          <p className="text-sm text-gray-700">Found {pickupLocations.length} pickup points. (UI for selection to be implemented)</p>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
            {JSON.stringify(pickupLocations, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ShippingMethodSection; 